import { type ItemDetails } from '@1debit/analytics';
import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, type ReactNode } from 'react';

import { ModalContent } from './ModalContent';
import {
	ModalClose,
	ModalFooter,
	ModalTitle,
	ModalTrigger,
	type ModalCloseProps as SubModalCloseProps,
	type ModalFooterProps as SubModalFooterProps,
	type ModalTitleProps as SubModalTitleProps,
	type ModalTriggerProps as SubModalTriggerProps,
} from './subcomponents';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { useModalOpenChangeAnalytics } from '../../client/AnalyticsProvider/hooks/useModalOpenChangeAnalytics';
import { classNames } from '../../server/utils/classNames';

export type ModalTriggerProps = SubModalTriggerProps;
export type ModalCloseProps = SubModalCloseProps;
export type ModalFooterProps = SubModalFooterProps;
export type ModalTitleProps = SubModalTitleProps;
export { type ModalContentProps } from './ModalContent';
export { type CloseButtonColorScheme } from './ModalContent';

export type ModalBodyProps = {
	children: ReactNode;
	className?: string;
};
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(({ children, className }, ref) => {
	return (
		<div
			className={classNames(
				'px-6 flex overflow-y-auto w-full flex-col grow sm:max-h-[250px] lg:max-h-[750px]',
				className,
			)}
			ref={ref}>
			{children}
		</div>
	);
});

export type ModalProps = Dialog.DialogProps & {
	analyticsId: AnalyticsId;
	itemDetails?: ItemDetails;
};

export const Modal = ({ analyticsId, onOpenChange, itemDetails, ...props }: ModalProps) => {
	const { handleModalOpenChangeAnalytics } = useModalOpenChangeAnalytics({
		analyticsId,
		defaultOpen: props.defaultOpen,
		onOpenChange,
		open: props.open,
		itemDetails,
	});

	return <Dialog.Root onOpenChange={handleModalOpenChangeAnalytics} {...props} />;
};

Modal.Trigger = ModalTrigger;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.Body = ModalBody;
Modal.Close = ModalClose;
Modal.Title = ModalTitle;
